import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Building2, MapPin, Calendar, Check } from "lucide-react";

const TONE_CLS = {
  good: "bg-good text-white",
  warn: "bg-warn text-white",
  muted: "bg-secondary text-ink border border-ink-line",
  ink: "bg-ink text-white",
};

export default function TesmDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [schema, setSchema] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: d }, { data: sch }] = await Promise.all([
          api.get(`/tesm/submissions/${id}`),
          api.get(`/modules/schema`),
        ]);
        setData(d);
        setSchema(sch?.tesm || null);
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      }
    })();
  }, [id]);

  const download = async () => {
    try {
      const resp = await api.get(`/tesm/submissions/${id}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tesm_${(data?.tender?.client || "submission").replace(/\s+/g, "_")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  if (!data || !schema) return <div className="text-sm text-ink-muted uppercase tracking-wider">Loading…</div>;

  const t = data.tender || {};
  const bandTone = data.band_tone || "muted";

  return (
    <div className="space-y-6" data-testid="tesm-detail-page">
      <button onClick={() => nav(-1)} className="text-[12px] uppercase tracking-wider text-ink-muted hover:text-ink flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to TESM submissions
      </button>

      {/* Header */}
      <div className="border-b border-ink-line pb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">TESM Submission</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">{t.client || "—"}</h1>
          <div className="text-base text-ink mt-1">{t.title || "—"}</div>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-ink-muted">
            <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {data.submitted_by_name || "—"}</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {t.location || "—"}</span>
            <span className="flex items-center gap-1.5 font-num"><Calendar className="w-3.5 h-3.5" /> {data.submitted_at ? new Date(data.submitted_at).toLocaleString() : "—"}</span>
            {t.sr_no != null && <span className="font-num text-xs">SR #{String(t.sr_no).padStart(3, "0")}</span>}
          </div>
        </div>
        <button onClick={download} data-testid="tesm-detail-export" className="flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-4 py-2.5 text-sm font-medium rounded-sm transition-colors">
          <Download className="w-4 h-4" /> Download CSV
        </button>
      </div>

      {/* Score + band */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-flat p-6 flex flex-col items-center justify-center" data-testid="tesm-detail-total">
          <div className="stat-label">Total score</div>
          <div className="font-num text-6xl font-bold mt-3 text-brand">{data.score}</div>
          <div className="text-[11px] uppercase tracking-widest text-ink-muted mt-2">/ {data.max_score}</div>
          <div className="text-xs text-ink-muted mt-3 font-num">{data.answered} / {schema.questions.length} answered</div>
        </div>
        <div className="card-flat p-6 lg:col-span-2">
          <div className="stat-label">Recommendation band</div>
          <div className="mt-2 flex items-center gap-3">
            {data.band ? (
              <span className={`inline-block px-3 py-1 text-sm font-display font-bold rounded-sm ${TONE_CLS[bandTone] || TONE_CLS.muted}`} data-testid="tesm-detail-band">
                {data.band}
              </span>
            ) : (
              <span className="text-ink-muted">—</span>
            )}
            <span className="text-sm text-ink">{data.band_desc}</span>
          </div>
          <div className="mt-5 pt-5 border-t border-ink-line text-[11px] text-ink-muted leading-relaxed">
            Bands · <span className="font-num">80–100</span> Strong &middot;
            <span className="font-num"> 60–79</span> Moderate &middot;
            <span className="font-num"> 40–59</span> Weak &middot;
            <span className="font-num"> 0–39</span> Poor
          </div>
          {data.notes && (
            <div className="mt-5 pt-5 border-t border-ink-line">
              <div className="stat-label mb-1">Notes</div>
              <div className="text-sm whitespace-pre-wrap text-ink">{data.notes}</div>
            </div>
          )}
        </div>
      </div>

      {/* Questions and selected answers */}
      <div className="card-flat" data-testid="tesm-detail-answers">
        <div className="px-6 py-4 border-b border-ink-line">
          <div className="stat-label">Responses</div>
          <div className="font-display font-bold text-lg tracking-tight mt-1">Question-by-question detail</div>
        </div>
        <div className="divide-y divide-ink-line">
          {schema.questions.map((q, idx) => {
            const selected = data.answers?.[q.key];
            return (
              <div key={q.key} className="px-6 py-4" data-testid={`tesm-detail-q-${q.key}`}>
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <div className="text-sm font-medium">
                    <span className="font-num text-ink-muted mr-2">{String(idx + 1).padStart(2, "0")}.</span>
                    {q.label}
                  </div>
                  <div className="font-num text-sm font-bold text-brand w-10 text-right">
                    {Number.isFinite(selected) ? selected : "—"}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((o) => {
                    const isSel = selected === o.score;
                    return (
                      <div
                        key={`${q.key}-${o.score}-${o.label}`}
                        className={`flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-sm border ${
                          isSel
                            ? "border-brand bg-brand/5 text-ink"
                            : "border-ink-line bg-white text-ink-muted"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                            isSel ? "border-brand bg-brand text-white" : "border-ink-line"
                          }`}>
                            {isSel && <Check className="w-3 h-3" />}
                          </span>
                          {o.label}
                        </span>
                        <span className={`font-num text-xs ${isSel ? "text-brand font-bold" : "text-ink-muted"}`}>
                          {o.score}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
